import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { BypassOtpBanner } from "../components/BypassOtpBanner";
import { OtpChannelPicker } from "../components/OtpChannelPicker";
import { OtpInput } from "../components/OtpInput";
import { ApiError } from "../lib/apiFetch";
import { otpSentMessage } from "../lib/otpCopy";
import type {
  OtpChannel,
  PartnerLoginResendRequest,
  PartnerLoginVerifyRequest,
  PartnerLoginVerifyResponse,
} from "../types/api";
import { AuthCard } from "./AuthCard";
import { usePortalApi } from "./usePortalApi";
import { usePortalAuthConfig } from "./usePortalAuthConfig";
import { usePortalSession } from "./usePortalSession";

/** Seconds the send button stays disabled; mirrors the server's `SEND_COOLDOWN_SECONDS`. */
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * State carried into `/verify-login` from the login submit.
 *
 * `challenge` is the opaque token the login returned, which verify and send
 * both need. `bypassSmsOtp` comes from the same response and drives the bypass
 * banner; missing and false mean the same. Unlike the first-time-setup step,
 * no password is handed off: `/login/verify` takes `{ challenge, code }` and
 * the server mints the session from the challenge claims. The challenge is not
 * a secret, since the OTP gates it, so it may survive a hard refresh; it stays
 * valid until its TTL.
 */
interface LoginVerifyState {
  challenge: string;
  bypassSmsOtp: boolean;
}

/**
 * Narrow the untyped `useLocation().state` (`unknown`) to our state shape.
 * Returns null when the shape doesn't match. A runtime guard, not an `as`-cast.
 */
function readState(state: unknown): LoginVerifyState | null {
  if (typeof state !== "object" || state === null) return null;
  const s = state as Record<string, unknown>;
  if (typeof s.challenge !== "string" || s.challenge.length === 0) return null;
  return { challenge: s.challenge, bypassSmsOtp: s.bypassSmsOtp === true };
}

/**
 * OTP entry for the login two-factor step (`/verify-login`).
 *
 * Login mints a challenge and routes here with it in router state; no code is
 * sent at that step. The user picks a delivery channel (text message or voice
 * call, text preselected) and presses Send code.
 * `<apiPrefix>/auth/login/resend` is the only send path, for the first send
 * and every resend, and targets the phone carried inside the challenge, never a
 * client-supplied one. The session is minted only when
 * `POST <apiPrefix>/auth/login/verify` accepts the OTP.
 *
 * - The code is 6 digits. Verify auto-submits once 6 digits are entered, and a
 *   busy guard blocks a double fire. The input appears only after a send or a
 *   429 on Send, since a still-valid code from the cooldown window can be typed
 *   directly.
 * - `session` on the verify response: store it and navigate to `/`.
 * - reason `"rejected"` or `"expired"`: show the matching error and clear the
 *   input.
 * - reason `"max_attempts_exceeded"`: restart at `/login`, where re-entering
 *   the email and password starts a fresh challenge.
 * - a dead or invalid challenge (400): show the dead-challenge state with a
 *   link back to sign-in.
 * - a 403: show the server's message verbatim.
 *
 * The cooldown starts at 0, since nothing is sent on arrival, and runs from the
 * first successful send; a 429 restarts it. The server enforces the real
 * per-phone cooldown, one window shared across both channels.
 *
 * The only guard is a missing or empty challenge in router state, which
 * redirects to `/login`. There is no password to drop, so success is a plain
 * `setSession` and `navigate("/")`.
 */
export function LoginVerify(): ReactNode {
  const api = usePortalApi();
  const { apiPrefix } = usePortalAuthConfig();
  const { setSession } = usePortalSession();
  const navigate = useNavigate();
  const location = useLocation();
  const carried = readState(location.state);

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deadChallenge, setDeadChallenge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  // `channel` is the delivery choice for the next send; the picker stays
  // visible after sending. `sentVia` is the channel of the last successful
  // send, null until one goes out, and drives the subtitle and button label.
  const [channel, setChannel] = useState<OtpChannel>("sms");
  const [sentVia, setSentVia] = useState<OtpChannel | null>(null);
  // True once a code is probably out there; gates the code input. Set on a
  // successful send and on a 429, since a code from the cooldown window may
  // still be valid. `sentVia` stays null on a 429 so the copy never claims a send.
  const [sent, setSent] = useState(false);
  // 0 on arrival, because login sends no code.
  const [cooldown, setCooldown] = useState(0);

  // The countdown synchronizes with wall-clock time, so it is an effect with an
  // interval. It re-arms whenever a send or a 429 raises `cooldown` again.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Guard the verify request against a double-fire (e.g. a paste that fills 6
  // digits while a previous request is still in flight). Mirrors `busy` but is
  // synchronous so two onChange ticks in the same frame can't both pass.
  const inFlight = useRef(false);

  // Without a challenge in router state, send the user back to start a fresh
  // login instead of rendering a broken form.
  if (!carried) {
    return <Navigate to="/login" replace />;
  }

  const { challenge, bypassSmsOtp } = carried;

  function verify(otp: string): void {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const body: PartnerLoginVerifyRequest = { challenge, code: otp };
    api<PartnerLoginVerifyResponse>(`${apiPrefix}/auth/login/verify`, {
      method: "POST",
      body: body as unknown as Record<string, unknown>,
    })
      .then((res) => {
        if (res.session) {
          // The OTP passed: store the session and leave for the home page.
          // `inFlight` and `busy` stay set because the screen is navigating away.
          setSession(res.session);
          void navigate("/", { replace: true });
          return;
        }
        // OTP not accepted: map the reason to a message or action.
        inFlight.current = false;
        setBusy(false);
        if (res.reason === "rejected") {
          setError("That code didn't match — check the newest code you received and try again.");
          setCode("");
          return;
        }
        if (res.reason === "expired") {
          setError("That code has expired — press “Resend code” below to get a new one.");
          setCode("");
          return;
        }
        if (res.reason === "max_attempts_exceeded") {
          // Too many tries: restart, since re-entering the email and password
          // starts a fresh challenge.
          void navigate("/login", { replace: true });
          return;
        }
        setError("Something went wrong. Please try again — if it keeps happening, contact us.");
        setCode("");
      })
      .catch((err: unknown) => {
        inFlight.current = false;
        setBusy(false);
        // A dead or invalid challenge (expired, or the wrong token type) 400s,
        // and the user must sign in again for a new one.
        if (err instanceof ApiError && err.status === 400) {
          setDeadChallenge(true);
          return;
        }
        // A 403 means the server refuses this account, e.g. one DearBill
        // disabled. Its user-facing detail is shown verbatim, as on Login.
        if (err instanceof ApiError && err.status === 403) {
          setError(err.detail);
          setCode("");
          return;
        }
        setError("Something went wrong. Please try again — if it keeps happening, contact us.");
        setCode("");
      });
  }

  function send(): void {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    const body: PartnerLoginResendRequest = { challenge, channel };
    api(`${apiPrefix}/auth/login/resend`, {
      method: "POST",
      body: body as unknown as Record<string, unknown>,
    })
      .then(() => {
        setSending(false);
        setSent(true);
        setSentVia(channel);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch((err: unknown) => {
        setSending(false);
        // A 429 means the server is still cooling down, and a code from that
        // window is still valid, so reveal the input. Restart the local
        // countdown without claiming a send.
        if (err instanceof ApiError && err.status === 429) {
          setSent(true);
          setCooldown(RESEND_COOLDOWN_SECONDS);
          return;
        }
        if (err instanceof ApiError && err.status === 400) {
          setDeadChallenge(true);
          return;
        }
        setError("We couldn't send the code. Press “Send code” to try again.");
      });
  }

  if (deadChallenge) {
    return (
      <AuthCard
        title="Verify your sign-in"
        footer={
          <Link to="/login" className="text-primary-text hover:text-ember-dark font-medium">
            Back to sign in
          </Link>
        }
      >
        <Alert tone="error" title="This sign-in attempt has expired. Please sign in again." />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Verify your sign-in"
      subtitle={
        sentVia
          ? `${otpSentMessage(sentVia)} Enter it below to finish signing in.`
          : "Choose how to get your 6-digit code, then enter it below to finish signing in."
      }
      footer={
        <Link to="/login" className="text-primary-text hover:text-ember-dark font-medium">
          Back to sign in
        </Link>
      }
    >
      <div className="space-y-5">
        <BypassOtpBanner bypassSmsOtp={bypassSmsOtp} />

        <OtpChannelPicker value={channel} onChange={setChannel} disabled={sending} />

        {sent && (
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              if (error) setError(null);
            }}
            onComplete={verify}
            disabled={busy}
            error={error !== null}
          />
        )}

        {error && <Alert tone="error" title={error} />}

        <Button
          type="button"
          variant={sentVia ? "secondary" : "primary"}
          size="lg"
          className="w-full"
          disabled={sending || cooldown > 0}
          onClick={send}
        >
          {sending
            ? "Sending…"
            : cooldown > 0
              ? `${sentVia ? "Resend" : "Send"} code in ${cooldown.toString()}s`
              : sentVia
                ? "Resend code"
                : "Send code"}
        </Button>
      </div>
    </AuthCard>
  );
}
